import { useState, useEffect } from 'react';
import { XIcon, CaretDownIcon, CaretUpIcon, FadersHorizontalIcon } from '@phosphor-icons/react';
import './styles.scss';

const FilterOverlay = ({
    isOpen,
    onClose,
    onApply,
    filters = {},
    activeFilters = {},
    sortOptions = [],
    activeSortOption = null,
    priceRange = null,
    minPrice = 0,
    maxPrice = 1000,
    onPriceChange,
    title = "SHOP"
}) => {
    const [localFilters, setLocalFilters] = useState(activeFilters);
    const [localSort, setLocalSort] = useState(activeSortOption);
    const [localPriceRange, setLocalPriceRange] = useState(() => {
        if (priceRange && priceRange[0] !== 0 && priceRange[1] !== Infinity) {
            return priceRange;
        }
        return [minPrice, maxPrice];
    });
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        setLocalFilters(activeFilters);
    }, [activeFilters]);

    useEffect(() => {
        setLocalSort(activeSortOption);
    }, [activeSortOption]);

    useEffect(() => {
        if (isOpen) {
            // Reset to actual min/max if priceRange has default values (0 and Infinity)
            if (priceRange && priceRange[0] !== 0 && priceRange[1] !== Infinity) {
                setLocalPriceRange(priceRange);
            } else {
                setLocalPriceRange(['', '']);
            }
        }
    }, [isOpen, priceRange, minPrice, maxPrice]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleFilterChange = (filterKey, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
    };

    const handleSortChange = (value) => {
        setLocalSort(value);
    };

    const handlePriceInputChange = (index, value) => {
        const newRange = [...localPriceRange];

        // Allow any value including empty string
        newRange[index] = value;
        setLocalPriceRange(newRange);
    };

    const handleApply = () => {
        // Validate and clamp price range before applying
        const validatedRange = [...localPriceRange];

        // Handle empty values
        if (validatedRange[0] === '' || isNaN(validatedRange[0])) {
            validatedRange[0] = minPrice;
        }
        if (validatedRange[1] === '' || isNaN(validatedRange[1])) {
            validatedRange[1] = maxPrice;
        }

        // Clamp values
        validatedRange[0] = Math.max(minPrice, Math.min(validatedRange[0], validatedRange[1]));
        validatedRange[1] = Math.min(maxPrice, Math.max(validatedRange[1], validatedRange[0]));

        onApply(localFilters, localSort);
        if (onPriceChange) {
            onPriceChange(validatedRange);
        }
        onClose();
    };

    const handleClear = () => {
        setLocalFilters({});
        setLocalSort(null);
        setLocalPriceRange(['', '']);
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`filter-overlay-backdrop ${isOpen ? 'open' : ''}`}
                onClick={onClose}
            />

            {/* Overlay Panel */}
            <div className={`filter-overlay ${isOpen ? 'open' : ''}`}>
                <div className="filter-overlay-header">
                    <h2><FadersHorizontalIcon size={24} /></h2>
                    <button className="close-button" onClick={onClose} aria-label="Close filter">
                        <XIcon size={24} />
                    </button>
                </div>

                <div className="filter-overlay-content">
                    {/* Sort By Section */}
                    {sortOptions && sortOptions.length > 0 && (
                        <div className="filter-section">
                            <h3 className="section-title">SORT BY</h3>
                            <div className="sort-options">
                                {sortOptions.map((option, index) => (
                                    <label key={index} className="custom-radio">
                                        <input
                                            type="radio"
                                            name="sort"
                                            value={option.value}
                                            checked={localSort === option.value}
                                            onChange={() => handleSortChange(option.value)}
                                        />
                                        <span className="radio-mark"></span>
                                        <span className="radio-label">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Filters Section */}
                    <div className="filter-section">
                        <h3 className="section-title">FILTERS</h3>

                        {Object.entries(filters).map(([filterKey, filterData]) => (
                            <div key={filterKey} className="filter-group">
                                <button
                                    className="filter-group-header"
                                    onClick={() => toggleSection(filterKey)}
                                >
                                    <span>{filterData.label}</span>
                                    {expandedSections[filterKey] ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />}
                                </button>

                                {expandedSections[filterKey] && (
                                    <div className="filter-group-content">
                                        {filterData.type === 'radio' && filterData.options.map((option, index) => (
                                            <label key={index} className="custom-radio">
                                                <input
                                                    type="radio"
                                                    name={filterKey}
                                                    value={option.value}
                                                    checked={localFilters[filterKey] === option.value}
                                                    onChange={() => handleFilterChange(filterKey, option.value)}
                                                />
                                                <span className="radio-mark"></span>
                                                <span className="radio-label">{option.label}</span>
                                            </label>
                                        ))}

                                        {filterData.type === 'select' && (
                                            <select
                                                className="custom-select"
                                                value={localFilters[filterKey] || ''}
                                                onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                                            >
                                                <option value="">All {filterData.label}</option>
                                                {filterData.options.map((option, index) => (
                                                    <option key={index} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}

                                        {filterData.type === 'price' && (
                                            <div className="price-inputs-container">
                                                <div className="price-input-group">
                                                    <label htmlFor="minPrice">Min</label>
                                                    <input
                                                        id="minPrice"
                                                        type="number"
                                                        className="price-input"
                                                        placeholder={minPrice.toString()}
                                                        value={localPriceRange[0] === '' ? '' : localPriceRange[0]}
                                                        onChange={(e) => handlePriceInputChange(0, e.target.value)}
                                                    />
                                                </div>
                                                <div className="price-input-group">
                                                    <label htmlFor="maxPrice">Max</label>
                                                    <input
                                                        id="maxPrice"
                                                        type="number"
                                                        className="price-input"
                                                        placeholder={maxPrice.toString()}
                                                        value={localPriceRange[1] === '' ? '' : localPriceRange[1]}
                                                        onChange={(e) => handlePriceInputChange(1, e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="filter-overlay-footer">
                    <button className="clear-button" onClick={handleClear}>
                        CLEAR
                    </button>
                    <button className="apply-button" onClick={handleApply}>
                        APPLY
                    </button>
                </div>
            </div>
        </>
    );
};

export default FilterOverlay;
